import { useEffect, useState } from 'react';
import { api } from './api';

function Bar({ label, views, max }) {
  const pct = max > 0 ? Math.round((views / max) * 100) : 0;
  return (
    <li className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="truncate font-semibold">{label}</span>
        <span className="shrink-0 text-brand-brown-soft">{views}</span>
      </div>
      <div className="mt-1 h-2 rounded-sm bg-brand-beige" role="img" aria-label={`${label}: ${views} Aufrufe`}>
        <div className="h-2 rounded-sm bg-brand-olive" style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
    </li>
  );
}

function Card({ title, rows, testid }) {
  const max = rows.length ? Math.max(...rows.map((r) => r.views)) : 0;
  return (
    <section className="border border-brand-line bg-brand-cream p-5" aria-label={title}>
      <h2 className="font-serif text-xl font-bold">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-brand-brown-soft">Noch keine Daten.</p>
      ) : (
        <ul className="mt-3" data-testid={testid}>{rows.map((r) => <Bar key={r.label} label={r.label} views={r.views} max={max} />)}</ul>
      )}
    </section>
  );
}

export default function Stats() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { api('/admin/stats').then(setData).catch((e) => setError(e.message)); }, []);

  if (error) return <p role="alert" className="rounded-sm border border-red-700 bg-red-50 p-4 font-semibold text-red-800">{error}</p>;
  if (!data) return <p role="status" className="text-brand-brown-soft">Lade Statistik …</p>;

  const tiles = [
    { label: 'Besucher heute', value: data.today.visitors, sub: `${data.today.views} Aufrufe`, testid: 'stat-today' },
    { label: 'Besucher gestern', value: data.yesterday.visitors, sub: `${data.yesterday.views} Aufrufe`, testid: 'stat-yesterday' },
    { label: 'Letzte 7 Tage', value: data.last7.visitors, sub: `${data.last7.views} Aufrufe`, testid: 'stat-7d' },
    { label: 'Letzte 30 Tage', value: data.last30.visitors, sub: `${data.last30.views} Aufrufe`, testid: 'stat-30d' },
  ];

  return (
    <div data-testid="admin-stats">
      <h1 className="font-serif text-3xl font-bold">Besucherstatistik</h1>
      <p className="mt-2 max-w-2xl text-sm text-brand-brown-soft">
        Datenschutzfreundliche Eigenmessung: keine Cookies, keine IP-Speicherung, keine Profile, keine Drittanbieter. Besucher werden über einen täglich wechselnden, nicht rückrechenbaren Hash gezählt.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="border border-brand-line bg-brand-cream p-5">
            <p className="text-sm font-semibold text-brand-brown-soft">{t.label}</p>
            <p className="mt-1 font-serif text-4xl font-bold" data-testid={t.testid}>{t.value}</p>
            <p className="text-xs text-brand-brown-soft">{t.sub}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title="Beliebteste Seiten (30 Tage)" rows={data.top_pages} testid="stats-top-pages" />
        <Card title="Beliebteste Rezepte (30 Tage)" rows={data.top_recipes} testid="stats-top-recipes" />
        <Card title="Einstiegsseiten (30 Tage)" rows={data.entry_pages} testid="stats-entry-pages" />
        <Card title="Referrer (30 Tage)" rows={data.referrers} testid="stats-referrers" />
        <Card title="Gerätetyp (30 Tage)" rows={data.devices} testid="stats-devices" />
        <Card title="Browser (30 Tage)" rows={data.browsers} testid="stats-browsers" />
      </div>
    </div>
  );
}
